import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import axios from "axios";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement
);

const Dashboard = ({ userDetails }) => {
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [chartData, setChartData] = useState({});
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [currentMonthBarData, setCurrentMonthBarData] = useState({});
  const [lastMonthBarData, setLastMonthBarData] = useState({});
  const [plots, setPlots] = useState(0);

  const navigate = useNavigate();

  const getWeekNumber = (date) => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const daysDifference = Math.floor(
      (date - startOfMonth) / (24 * 60 * 60 * 1000)
    );
    return Math.ceil((daysDifference + startOfMonth.getDay() + 1) / 7);
  };

  window.addEventListener("beforeunload", () => {
    localStorage.removeItem("sale_history");
    localStorage.removeItem("all_clients");
  });

  // Fetch Sales
  const fetchSales = async () => {
    try {
      const response = await axios.get(
        "https://api.swalokgroup.com/api/get-sale-invoice"
      );
      setSales(response.data);
      localStorage.setItem("sale_history", JSON.stringify(response.data));
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch clients
  const fetchClients = async () => {
    try {
      const response = await axios.get(
        "https://api.swalokgroup.com/api/get-all-clients"
      );
      setClients(response.data);
      localStorage.setItem("all_clients", JSON.stringify(response.data));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch all data in parallel
        await Promise.all([fetchClients(), fetchSales()]);
      } catch (error) {
        console.log(error);
      }
    };

    // Check if data exists in localStorage to prevent re-fetching
    const storedClients = localStorage.getItem("all_clients");
    const storedSales = localStorage.getItem("sale_history");
    if (!storedSales || !storedClients) {
      loadData();
    } else {
      setClients(JSON.parse(storedClients));
      setSales(JSON.parse(storedSales));
    }
  }, [setClients, setSales]);

  // Options for the pie chart
  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "right",
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  // Options for the bar chart
  const barOptions = {
    responsive: true,
    scales: {
      y: {
        title: {
          display: true,
          text: "Amount (₹)",
        },
        beginAtZero: true,
      },
    },
    plugins: {
      tooltip: {
        enabled: true,
      },
      legend: {
        display: false,
      },
    },
  };

  useEffect(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    const currentMonthSales = sales.filter((sale) => {
      const saleDate = new Date(sale.sale_date);
      return (
        saleDate.getMonth() === currentMonth &&
        saleDate.getFullYear() === currentYear
      );
    });

    const lastMonthSales = sales.filter((sale) => {
      const saleDate = new Date(sale.sale_date);
      return (
        saleDate.getMonth() === lastMonth && saleDate.getFullYear() === lastYear
      );
    });

    setTotalSalesAmount(
      sales.reduce((sum, obj) => sum + parseFloat(obj.received), 0)
    );

    // Process sales data for current and last month
    const processSalesData = (sales) => {
      const categoryCounts = {};
      let totalSales = 0;
      const weekSales = {};

      sales.forEach((sale) => {
        if (sale.booking_type) {
          categoryCounts[sale.booking_type] =
            (categoryCounts[sale.booking_type] || 0) + 1;
        }
        if (sale.received) {
          totalSales += parseFloat(sale.received);
        }

        const saleDate = new Date(sale.sale_date);
        const weekNumber = getWeekNumber(saleDate);
        const monthName = saleDate.toLocaleString("default", {
          month: "short",
        });
        const weekKey = `${monthName} ${saleDate.getFullYear()}(W${weekNumber})`;

        if (sale.received) {
          weekSales[weekKey] =
            (weekSales[weekKey] || 0) + parseFloat(sale.received);
        }
      });

      const sortedWeekSales = Object.entries(weekSales)
        .sort(([weekA], [weekB]) => new Date(weekA) - new Date(weekB))
        .reduce((acc, [week, sales]) => {
          acc[week] = sales;
          return acc;
        }, {});

      return { totalSales, categoryCounts, sortedWeekSales };
    };

    // Prepare current month and last month sales data
    const currentMonthData = processSalesData(currentMonthSales);
    const lastMonthData = processSalesData(lastMonthSales);

    // Set chart data for the current month sales
    const currentMonthLabels = Object.keys(currentMonthData.categoryCounts);
    const currentMonthValues = Object.values(currentMonthData.categoryCounts);
    setChartData({
      labels: currentMonthLabels,
      datasets: [
        {
          label: "Total",
          data: currentMonthValues,
          backgroundColor: ["#88D66C", "#00ADE0", "#ffcd56"],
          hoverBackgroundColor: ["#5CB338", "#0162C9", "#ffb900"],
        },
      ],
    });

    // Set bar chart data for the current month
    setCurrentMonthBarData({
      labels: Object.keys(currentMonthData.sortedWeekSales),
      datasets: [
        {
          label: "Sales Amount",
          data: Object.values(currentMonthData.sortedWeekSales),
          backgroundColor: "#FF5733",
          borderColor: "#FF5733",
          borderWidth: 1,
        },
      ],
    });

    // Set bar chart data for the last month
    setLastMonthBarData({
      labels: Object.keys(lastMonthData.sortedWeekSales),
      datasets: [
        {
          label: "Sales Amount",
          data: Object.values(lastMonthData.sortedWeekSales),
          backgroundColor: "pink",
          borderColor: "#FF5733",
          borderWidth: 1,
        },
      ],
    });
  }, [sales, userDetails.client_id]);

  // Fetch total plots data
  useEffect(() => {
    const fetchPlots = async () => {
      try {
        const response = await fetch(`https://api.swalokgroup.com/api/get-plots`);
        if (!response.ok) {
          throw new Error("Failed to fetch reports");
        }
        const data = await response.json();
        setPlots(data);
        let totalPlots = 0;

        data.forEach((plot) => {
          if (plot.id) {
            totalPlots = totalPlots + 1;
          }
        });
        setPlots(totalPlots);
      } catch (error) {
        console.log(error.message);
      }
    };

    fetchPlots();
  }, []);

  return (
    <>
      <section className="py-5 px-4 container">
        <p className="sec-heading">Swalok Group</p>

        <div className="row mb-4">
          <div className="col-lg-9 col-12">
            <div className="row">
              <div className="col-lg-6 col-12 my-2">
                <div className="chart-container">
                  <div className="sec-subheading mb-3">
                    <hr />
                    <span className="text-capitalize">Current Month Sales</span>
                    <hr />
                  </div>
                  {sales.length > 0 && (
                    <Bar data={currentMonthBarData} options={barOptions} />
                  )}
                </div>
              </div>

              <div className="col-lg-6 col-12 my-2">
                <div className="chart-container">
                  <div className="sec-subheading mb-3">
                    <hr />
                    <span className="text-capitalize">Last Month Sales</span>
                    <hr />
                  </div>
                  {sales.length > 0 && (
                    <Bar data={lastMonthBarData} options={barOptions} />
                  )}
                </div>
              </div>

              <div className="col-lg-6 col-12 my-2">
                <div className="chart-container">
                  <div className="sec-subheading mb-3">
                    <hr />
                    <span className="text-capitalize">Recent Plot Sale</span>
                    <hr />
                  </div>
                  {sales.length > 0 && (
                    <Pie
                      className="m-auto"
                      data={chartData}
                      options={pieOptions}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-12">
            <div className="row">
              <div className="col-lg-12 col-md-6 col-12 my-2">
                <div className="chart-container2">
                  <i className="fa-solid my-2 fa-coins chart-icon"></i>
                  <p className="fw-bold">Total Sales</p>
                  <p className="my-2 fw-bold">₹ {totalSalesAmount}</p>
                  <p
                    className="mx-3 px-3 py-2 text-black cursor-pointer"
                    style={{
                      backgroundColor: "whitesmoke",
                      borderRadius: "8px",
                    }}
                    onClick={() => navigate("/admin/sale-history")}
                  >
                    View Sales
                  </p>
                </div>
              </div>

              <div className="col-lg-12 col-md-6 col-12 my-2">
                <div className="chart-container2">
                  <i className="fa-solid my-2 fa-house-chimney chart-icon"></i>
                  <p className="fw-bold">Total Plots</p>
                  <p className="my-2 fw-bold">{plots}</p>
                  <p
                    className="mx-3 px-3 py-2 text-black cursor-pointer"
                    style={{
                      backgroundColor: "whitesmoke",
                      borderRadius: "8px",
                    }}
                    onClick={() => navigate("/admin/plot-inventory")}
                  >
                    View Plots
                  </p>
                </div>
              </div>

              <div className="col-lg-12 col-md-6 col-12 my-2">
                <div className="chart-container2">
                  <i className="fa-solid my-2 fa-house-chimney chart-icon"></i>
                  <p className="fw-bold">Total Team Members</p>
                  <p className="my-2 fw-bold">{clients.length - 1}</p>
                  <p
                    className="mx-3 px-3 py-2 text-black cursor-pointer"
                    style={{
                      backgroundColor: "whitesmoke",
                      borderRadius: "8px",
                    }}
                    onClick={() => navigate("/admin/team")}
                  >
                    View Team
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Dashboard;
